import React from 'react';
import {
  IconTwitter,
  IconInstagram,
  VisuallyHidden,
  TypeList,
  TypeListItem,
} from '@spotify-internal/encore-web';
import { ResponsiveLogo, Link } from '../../ui';
import androidIcon from '../images/footer-android.svg';
import iosIcon from '../images/footer-ios.svg';
import {
  FooterWrapper,
  FooterSection,
  SectionHeader,
  InlineList,
  CircleIcon,
  Copyright,
} from './Footer.styles';
import { useT } from '@spotify-internal/i18n-core';
import { useFooterLabels } from './useFooterLabels';

const TRACK_CAT = 'Footer';
const YEAR = new Date().getFullYear();

export const Footer: React.FC = () => {
  const t = useT();
  const footerLabels = useFooterLabels();
  return (
    <FooterWrapper>
      <FooterSection>
        <Link
          href="/?ref=footer"
          track={[TRACK_CAT, 'Logo']}
          highlight
          data-testid="footer-logo"
        >
          <ResponsiveLogo />
        </Link>
      </FooterSection>

      <FooterSection>
        <SectionHeader>
          {t(
            'FRODOR_FINE_PRINT_FOOTER',
            'Fine Print',
            'Fine Print refers to terms and conditions',
          )}
        </SectionHeader>
        <TypeList.ul>
          <TypeListItem>
            <Link
              data-testid="footer-legal"
              href="https://www.spotify.com/legal/spotify-for-artists-terms-and-conditions/"
              external
              track={[TRACK_CAT, 'Legal']}
            >
              {t(
                'FRODOR_LEGAL_LINK_TEXT_FOOTER',
                'Legal',
                'Legal refers to terms and conditions',
              )}
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-privacy"
              href="https://www.spotify.com/legal/privacy-policy"
              external
              track={[TRACK_CAT, 'Privacy Policy']}
            >
              {t('FRODOR_a92e8f', 'Privacy Policy', '')}
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-cookies"
              href="https://www.spotify.com/legal/cookies-policy/"
              external
              track={[TRACK_CAT, 'Cookies']}
            >
              {t(
                'FRODOR_COOKIES_LINK_TEXT_FOOTER',
                'Cookies',
                'Cookies are a user tracking method for the web',
              )}
            </Link>
          </TypeListItem>
        </TypeList.ul>
      </FooterSection>

      <FooterSection>
        <SectionHeader>{t('FRODOR_7bfbb4', 'Need help?', '')}</SectionHeader>
        <TypeList.ul>
          <TypeListItem>
            <Link
              href="/contact"
              track={[TRACK_CAT, 'Contact Us']}
              data-testid="footer-contact"
            >
              {t('FRODOR_51f8e4', 'Contact Us', '')}
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-claim"
              href="/claim"
              track={[TRACK_CAT, 'Get Access']}
            >
              {t('FRODOR_d84abd', 'Get Access', '')}
            </Link>
          </TypeListItem>
        </TypeList.ul>
      </FooterSection>

      <FooterSection>
        <SectionHeader>{t('FRODOR_d0bb36', 'Creator tools', '')}</SectionHeader>
        <TypeList.ul>
          <TypeListItem>
            <Link
              data-testid="footer-anchor"
              external
              href="https://app.adjust.com/iya18ah?redirect=https%3A%2F%2Fanchor.fm%2Fapp"
              track={[TRACK_CAT, 'Creator Tools - Anchor']}
            >
              {t('FRODOR_256bed', 'Anchor', '')}
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-soundtrap"
              external
              href="https://www.soundtrap.com/?utm_source=spotify_site&utm_medium=footer_menulink&utm_campaign=alwayson_global_soundtrap_destination"
              track={[TRACK_CAT, 'Creator Tools - Soundtrap']}
            >
              {t('FRODOR_4fdeca', 'Soundtrap', '')}
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-s4p"
              external
              href="https://podcasters.spotify.com/?utm_source=artists.spotify.com"
              track={[TRACK_CAT, 'Creator Tools - Spotify for Podcasters']}
            >
              {t('FRODOR_8e3734', 'Spotify for Podcasters', '')}
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-spa"
              external
              href="https://noteable.spotify.com/?utm_source=artists.spotify.com"
              track={[TRACK_CAT, 'Creator Tools - Noteable']}
            >
              Noteable
            </Link>
          </TypeListItem>
        </TypeList.ul>
      </FooterSection>

      <FooterSection>
        <SectionHeader>
          {t(
            'FRODOR_FOLLOW_US_FOOTER',
            'Follow us',
            'Follow refers to a feature of Twitter',
          )}
        </SectionHeader>
        <InlineList>
          <TypeListItem>
            <Link
              data-testid="footer-twitter"
              highlight
              external
              href="https://twitter.com/spotifyartists"
              track={[TRACK_CAT, 'Social - Twitter']}
            >
              <CircleIcon as={IconTwitter} aria-label="Twitter" />
              <VisuallyHidden>Twitter</VisuallyHidden>
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-instagram"
              highlight
              external
              href="https://www.instagram.com/spotifyforartists/"
              track={[TRACK_CAT, 'Social - Instagram']}
            >
              <CircleIcon as={IconInstagram} aria-label="Instagram" />
              <VisuallyHidden>Instagram</VisuallyHidden>
            </Link>
          </TypeListItem>
        </InlineList>
      </FooterSection>

      <FooterSection>
        <SectionHeader>
          {t(
            'FRODOR_GET_THE_APP_SECTION_HEADER_FOOTER',
            'Get the app',
            'App is short for application',
          )}
        </SectionHeader>
        <TypeList.ul>
          <TypeListItem>
            <Link
              data-testid="footer-ios"
              highlight
              external
              href="https://itunes.apple.com/us/app/spotify-for-artists/id1222021797"
              track={[TRACK_CAT, 'iOS App']}
            >
              <img
                data-src={iosIcon}
                alt={footerLabels.appStore}
                className="lazyload"
              />
            </Link>
          </TypeListItem>
          <TypeListItem>
            <Link
              data-testid="footer-android"
              highlight
              external
              href="https://play.google.com/store/apps/details?id=com.spotify.s4a"
              track={[TRACK_CAT, 'Android App']}
            >
              <img
                data-src={androidIcon}
                alt={footerLabels.googlePlay}
                className="lazyload"
              />
            </Link>
          </TypeListItem>
        </TypeList.ul>
      </FooterSection>

      <FooterSection>
        <Copyright data-testid="footer-copyright">
          &copy; {YEAR} Spotify AB
        </Copyright>
      </FooterSection>
    </FooterWrapper>
  );
};
